import { DEFAULT_EXCHANGE_RATE_THRESHOLD } from '@features/paymentReconciliationModal/elements/ExchangeRateDeviationWarning/constants'
import { calculateExchangeRateDeviation } from '@features/paymentReconciliationModal/elements/ExchangeRateDeviationWarning/utils'
import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { roundAmount } from '@features/reconciliation/utils/roundAmount'
import { BankLine } from '@modules-deprecated/bankReconciliation/types'

import { getTransactionAmount } from '../../../utils/getTransactionAmount'

export interface CurrencyMismatchData {
  bankLineAmount: number
  currencyId: string
  deviation: number
  exchangeRate: number
  subjectAmount: number
}

interface GetCurrencyMismatchDataParams {
  accountCurrency?: string
  selectedBookkeepingItems: BookkeepingItem[]
  selectedTransaction: BankLine | null
  combinedTransactions?: BankLine[]
}

export const getCurrencyMismatchData = ({
  accountCurrency,
  selectedBookkeepingItems,
  selectedTransaction,
  combinedTransactions,
}: GetCurrencyMismatchDataParams): CurrencyMismatchData | null => {
  if (!selectedTransaction || !accountCurrency) {
    return null
  }

  const crossCurrencyItems = selectedBookkeepingItems.filter(
    (item) => item.currency && item.currency !== accountCurrency,
  )

  // Skip warning when selection mixes account-currency and foreign-currency items.
  // In those cases the bank line amount cannot be compared only to foreign totals.
  if (crossCurrencyItems.length !== selectedBookkeepingItems.length) {
    return null
  }

  if (crossCurrencyItems.length === 0) {
    return null
  }

  const [firstItem] = crossCurrencyItems
  const currencyId = firstItem.currency

  // Only compute deviation when all selected items share the same foreign currency.
  if (crossCurrencyItems.some((item) => item.currency !== currencyId)) {
    return null
  }

  const exchangeRate = firstItem.exchangeRate

  if (!currencyId || !exchangeRate || exchangeRate <= 0) {
    return null
  }

  const totalForeignAmount = roundAmount(crossCurrencyItems.reduce((sum, item) => sum + item.amount, 0))

  if (totalForeignAmount === 0) {
    return null
  }

  const bankLineAmount = getTransactionAmount(selectedTransaction, combinedTransactions)
  const deviation = calculateExchangeRateDeviation(totalForeignAmount, exchangeRate, bankLineAmount)

  if (deviation < DEFAULT_EXCHANGE_RATE_THRESHOLD) {
    return null
  }

  return {
    bankLineAmount,
    deviation,
    exchangeRate,
    subjectAmount: totalForeignAmount,
    currencyId,
  }
}
